/*
  Minimal Stimulus lazy loader for Webpack:
  - Observes DOM for [data-controller]
  - For each identifier, dynamically imports app/javascript/controllers/<path>_controller.js
  - Registers the controller once per identifier
  - Uses parallel loading for better performance
*/

import { debug } from '../../../app/javascript/utils/debug';

const loadedControllerIdentifiers = (window.__loadedStimulusIds = window.__loadedStimulusIds || new Set());

// Async (lazy) context: webpack will split each controller into its own chunk
const controllersContext = require.context(
  '../../../app/javascript/controllers',
  true,
  /_controller\.js$/,
  'lazy'
);

// Build identifier -> request map (e.g., "video-player" -> "./video/player_controller.js")
const identifierToRequest = {};
controllersContext.keys().forEach((key) => {
  const identifier = key
    .replace(/^\.\//, '')
    .replace(/_controller\.js$/, '')
    .replace(/\//g, '-')
    .replace(/_/g, '-');
  identifierToRequest[identifier] = key;
});
// Available in window.__stimulusControllers for debugging
window.__stimulusControllers = Object.keys(identifierToRequest).sort();

async function loadAndRegisterController(application, identifier) {
  // Already loaded check
  if (loadedControllerIdentifiers.has(identifier)) {
    return;
  }

  const request = identifierToRequest[identifier];
  if (!request) {
    debug.warn('stimulus-lazy', `Unknown controller: "${identifier}"`);
    return;
  }

  try {
    // Optimistic locking - mark as loaded BEFORE async load to prevent race conditions
    loadedControllerIdentifiers.add(identifier);

    debug('stimulus-lazy', `Loading "${identifier}"...`);
    const module = await controllersContext(request);
    if (module?.default) {
      application.register(identifier, module.default);
      debug('stimulus-lazy', `Registered "${identifier}"`);
    }
  } catch (error) {
    // Unlock on failure so it can be retried
    loadedControllerIdentifiers.delete(identifier);
    debug.error('stimulus-lazy', `Failed to load "${identifier}":`, error);
  }
}

function extractIdentifiersFrom(el) {
  const attr = el.getAttribute?.('data-controller');
  if (!attr) return [];
  return attr.split(/\s+/).map(s => s.trim()).filter(Boolean);
}

/**
 * Parallel scanner - collects all unique identifiers and loads them in parallel
 */
async function scanAndLoad(application, root = document, source = 'scan') {
  const elements = root.querySelectorAll('[data-controller]');
  if (elements.length === 0) return;

  // Collect all unique identifiers that need loading
  const identifiersToLoad = new Set();
  elements.forEach(el => {
    extractIdentifiersFrom(el).forEach(id => {
      if (!loadedControllerIdentifiers.has(id)) {
        identifiersToLoad.add(id);
      }
    });
  });

  if (identifiersToLoad.size === 0) return;

  debug('stimulus-lazy', `[${source}] Loading ${identifiersToLoad.size} controllers in parallel:`, Array.from(identifiersToLoad));

  // Load all controllers in parallel - much faster than sequential
  const promises = Array.from(identifiersToLoad).map(id =>
    loadAndRegisterController(application, id)
  );
  await Promise.all(promises);
}

// Global singleton flag - prevents multiple observers/listeners
let _isLazyLoaderInitialized = false;

export function enableStimulusLazyLoad(application) {
  // Singleton guard - prevent multiple initializations
  if (_isLazyLoaderInitialized) {
    debug('stimulus-lazy', 'Already initialized, skipping');
    return null;
  }
  _isLazyLoaderInitialized = true;

  debug('stimulus-lazy', 'Initializing lazy loader (singleton)');

  // Initial scan
  scanAndLoad(application, document, 'initial');

  // Delayed scan for elements added during initial render
  setTimeout(() => {
    scanAndLoad(application, document, 'delayed-initial');
  }, 200);

  // Turbo navigation handlers
  document.addEventListener('turbo:load', () => {
    debug('stimulus-lazy', 'turbo:load event fired');
    scanAndLoad(application, document, 'turbo:load');
  });

  document.addEventListener('turbo:frame-render', (event) => {
    debug('stimulus-lazy', 'turbo:frame-render event fired');
    scanAndLoad(application, event.target, 'turbo:frame-render');
  });

  // MutationObserver for dynamic content
  let pendingMutationScan = false;

  const observer = new MutationObserver(mutations => {
    // Check if any mutations added nodes with controllers
    let hasNewControllers = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('[data-ignore-stimulus], [data-ignore-stimulus] *')) continue;
        if (node.hasAttribute('data-controller') || node.querySelector('[data-controller]')) {
          hasNewControllers = true;
          break;
        }
      }
      if (hasNewControllers) break;
    }

    if (!hasNewControllers || pendingMutationScan) return;

    // Debounce mutation scans
    pendingMutationScan = true;
    const scan = () => {
      pendingMutationScan = false;
      scanAndLoad(application, document.body, 'mutation');
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(scan, { timeout: 120 });
    } else {
      setTimeout(scan, 50);
    }
  });

  const observeRoot = document.body || document.documentElement;
  observer.observe(observeRoot, { childList: true, subtree: true });

  return observer;
}

export default enableStimulusLazyLoad;
